import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Button,
  Breadcrumbs,
  Link,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Paper
} from '@mui/material';
import {
  Folder as FolderIcon,
  Image as ImageIcon,
  Search as SearchIcon,
  Home as HomeIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  ZoomIn as ZoomInIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import api from '../services/api';
import { S3Object, S3Folder, S3BrowseResult } from '../types';

// =============================================================================
// INTERFACES
// =============================================================================
interface BreadcrumbItem {
  name: string;
  prefix: string;
}

// =============================================================================
// COMPONENT
// =============================================================================
export default function S3ImageManager() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPrefix, setCurrentPrefix] = useState('');
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([]);
  const [images, setImages] = useState<S3Object[]>([]);
  const [folders, setFolders] = useState<S3Folder[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [nextToken, setNextToken] = useState<string | undefined>();
  const [page, setPage] = useState(1);
  const [selectedImage, setSelectedImage] = useState<S3Object | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  // =============================================================================
  // EFFECTS
  // =============================================================================
  useEffect(() => {
    loadBucketContents();
  }, [currentPrefix]);

  useEffect(() => {
    updateBreadcrumbs();
  }, [currentPrefix]);

  // =============================================================================
  // HELPER FUNCTIONS
  // =============================================================================
  const updateBreadcrumbs = () => {
    if (!currentPrefix) {
      setBreadcrumbs([]);
      return;
    }

    const parts = currentPrefix.split('/').filter(Boolean);
    const crumbs: BreadcrumbItem[] = [];
    let prefix = '';

    parts.forEach((part, index) => {
      prefix += part + '/';
      crumbs.push({
        name: part,
        prefix: prefix
      });
    });

    setBreadcrumbs(crumbs);
  };

  const loadBucketContents = async (continuationToken?: string) => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        prefix: currentPrefix,
        imagesOnly: 'true',
        loadAll: 'true' // Load all images for better search functionality
      });

      // Note: When loadAll is true, continuationToken is ignored by the backend
      const response = await api.get(`/upload/browse?${params}`);
      const data: S3BrowseResult = response.data;

      setImages(data.objects);
      setFolders(data.folders);
      setHasMore(data.hasMore);
      setNextToken(data.nextToken);
    } catch (err: any) {
      console.error('Failed to load S3 contents:', err);
      setError(err.response?.data?.error || 'Failed to load bucket contents');
    } finally {
      setLoading(false);
    }
  };

  const handleFolderClick = (folder: S3Folder) => {
    setCurrentPrefix(folder.prefix);
    setPage(1);
  };

  const handleBreadcrumbClick = (prefix: string) => {
    setCurrentPrefix(prefix);
    setPage(1);
  };

  const handleHomeClick = () => {
    setCurrentPrefix('');
    setPage(1);
  };

  const handleRefresh = () => {
    loadBucketContents();
  };

  const handleImagePreview = (image: S3Object) => {
    setSelectedImage(image);
    setPreviewOpen(true);
  };

  const handleCopyUrl = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      // You might want to show a toast notification here
    } catch (err) {
      console.error('Failed to copy URL:', err);
    }
  };

  const handleDeleteImage = async (image: S3Object) => {
    if (!window.confirm(`Are you sure you want to delete "${image.fileName}"?`)) {
      return;
    }

    try {
      await api.delete('/upload/delete', {
        data: { url: image.url }
      });
      
      // Remove from local state
      setImages(prev => prev.filter(img => img.key !== image.key));
    } catch (err: any) {
      console.error('Failed to delete image:', err);
      setError(err.response?.data?.error || 'Failed to delete image');
    }
  };



  const filteredImages = images.filter(image =>
    image.fileName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const paginatedImages = filteredImages.slice((page - 1) * 50, page * 50);
  
  // Reset to page 1 when search term changes
  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // =============================================================================
  // RENDER
  // =============================================================================
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Image Manager
      </Typography>

      {/* Navigation */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Tooltip title="Home">
            <IconButton onClick={handleHomeClick} size="small">
              <HomeIcon />
            </IconButton>
          </Tooltip>
          <Breadcrumbs sx={{ ml: 1, flexGrow: 1 }}>
            {breadcrumbs.map((crumb, index) => (
              <Link
                key={index}
                component="button"
                variant="body2"
                onClick={() => handleBreadcrumbClick(crumb.prefix)}
                sx={{ textDecoration: 'none' }}
              >
                {crumb.name}
              </Link>
            ))}
          </Breadcrumbs>
          <Tooltip title="Refresh">
            <IconButton onClick={handleRefresh} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <TextField
          fullWidth
          size="small"
          placeholder="Search images..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {/* Stats */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Chip
          label={`${folders.length} Folders`}
          color="primary"
          variant="outlined"
        />
        <Chip
          label={`${filteredImages.length} Images`}
          color="secondary"
          variant="outlined"
        />
      </Box>

      {/* Content */}
      {loading && images.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : (
        <Box>
          {/* Folders */}
          {folders.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Folders ({folders.length})
              </Typography>
              <Grid container spacing={2}>
                {folders.map((folder) => (
                  <Grid item xs={6} sm={4} md={3} lg={2} key={folder.prefix}>
                    <Card
                      sx={{
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'action.hover' }
                      }}
                      onClick={() => handleFolderClick(folder)}
                    >
                      <CardContent sx={{ textAlign: 'center', py: 2 }}>
                        <FolderIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                        <Typography variant="body2" noWrap>
                          {folder.name}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {/* Images */}
          {filteredImages.length > 0 ? (
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Images ({filteredImages.length})
              </Typography>
              <Grid container spacing={2}>
                {paginatedImages.map((image) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={image.key}>
                    <Card>
                      <CardMedia
                        component="img"
                        height="200"
                        image={image.url}
                        alt={image.fileName}
                        sx={{ objectFit: 'cover', cursor: 'pointer' }}
                        onClick={() => handleImagePreview(image)}
                      />
                      <CardContent sx={{ p: 2 }}>
                        <Typography variant="subtitle2" noWrap>
                          {image.fileName}
                        </Typography>
                        <Typography variant="caption" display="block" color="text.secondary">
                          {formatFileSize(image.size)}
                        </Typography>
                        <Typography variant="caption" display="block" color="text.secondary">
                          {formatDate(image.lastModified)}
                        </Typography>
                      </CardContent>
                      <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                        <Box>
                          <Tooltip title="Preview">
                            <IconButton
                              size="small"
                              onClick={() => handleImagePreview(image)}
                            >
                              <ZoomInIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Copy URL">
                            <IconButton
                              size="small"
                              onClick={() => handleCopyUrl(image.url)}
                            >
                              <CopyIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteImage(image)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              {/* Pagination */}
              {Math.ceil(filteredImages.length / 50) > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Pagination
                    count={Math.ceil(filteredImages.length / 50)}
                    page={page}
                    onChange={(_, newPage) => setPage(newPage)}
                    color="primary"
                  />
                </Box>
              )}


            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <ImageIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
              <Typography color="text.secondary">
                {searchTerm ? 'No images match your search' : 'No images found in this folder'}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Image Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            {selectedImage?.fileName}
          </Typography>
          <IconButton onClick={() => setPreviewOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {selectedImage && (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <img
                src={selectedImage.url}
                alt={selectedImage.fileName}
                style={{
                  maxWidth: '100%',
                  maxHeight: '70vh',
                  objectFit: 'contain'
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => selectedImage && handleCopyUrl(selectedImage.url)}>
            Copy URL
          </Button>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
} 